import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  placeAddModalRecoil,
  placeModifyModalRecoil,
} from '@/atom/ModalAtom';
import {
  placeListRecoil,
  placeModifyNumRecoil,
  placeListLengthRecoil,
} from '@/atom/PlaceAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { ApiService } from '@/api';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import PlaceAddModal from '@/modal/PlaceAddModal';
import PlaceModifyModal from '@/modal/PlaceModifyModal';
import { PlaceDto, PlaceSearchDto } from '@/model/place.dto';
import ExcelDownloadButton from '@/component/ExcelDownloadButton';

const PlaceManagement = () => {
  const accessToken = sessionStorage.getItem('accessToken');
  const api = useMemo(() => new ApiService(), []);
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [placeAddModalState, setPlaceAddModalState] =
    useRecoilState(placeAddModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [placeModifyModalState, setPlaceModifyModalState] = useRecoilState(
    placeModifyModalRecoil,
  );
  const [placeListState, setPlaceListState] = useRecoilState(placeListRecoil);
  const [placeModifyNumState, setPlaceModifyNumState] =
    useRecoilState(placeModifyNumRecoil);
  const [placeListLengthState, setPlaceListLengthState] = useRecoilState(
    placeListLengthRecoil,
  );

  const [placeId, setPlaceId] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [manager, setManager] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [detail, setDetail] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [placeAllList, setPlaceAllList] = useState<PlaceDto[]>([]);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const fetchPlaceList = async () => {
    const dto: PlaceSearchDto = {
      place_id: placeId,
      name,
      manager,
      address,
      detail,
    };
    try {
      const response: PlaceDto[] = await api.getPlaceList(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      setPlaceListLengthState(response.length);
      setPlaceAllList(response);
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const filterPlaceList = async () => {
    const dto: PlaceSearchDto = {
      place_id: placeId,
      name,
      manager,
      address,
      detail,
      offset,
      limit,
    };
    try {
      const response: PlaceDto[] = await api.getFilterPlaceList(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      setPlaceListState(response);
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    fetchPlaceList();
    filterPlaceList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleDashboardCheckBoxChange = async (
    place: PlaceDto,
    checkVlaue: boolean,
  ) => {
    const dto: PlaceDto = {
      name: place.name,
      manager: place.manager,
      phone: place.phone,
      address: place.address,
      detail: place.detail,
      is_active: checkVlaue,
    };
    try {
      await api.putPlaceModify(String(accessToken), Number(place.id), dto);
      filterPlaceList();
      fetchPlaceList();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handlePlaceModify = (id: number) => {
    setPlaceModifyNumState(id);
    setPlaceModifyModalState(true);
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      placeListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectModifyNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    if (checkItems.length > 1) {
      setErrorModalText('다중 수정은 불가합니다.');
      return;
    }
    handlePlaceModify(checkItems[0]);
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      await api.delPlaceList(
        String(sessionStorage.getItem('accessToken')),
        checkItems,
      );
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    filterPlaceList();
    fetchPlaceList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="출입장소 관리" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputArea>
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="출입장소"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <IS.TextInputType1
                style={{ width: 320 }}
                type="text"
                placeholder="주소"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="관리자"
                value={manager}
                onChange={(e) => setManager(e.target.value)}
              />
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="출입장소 번호"
                value={placeId}
                onChange={(e) => setPlaceId(e.target.value)}
              />
            </InputArea>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{placeListLengthState}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type1
              type="button"
              onClick={() => setPlaceAddModalState(true)}
            >
              등록
            </BS.Btn1Type1>
            <BS.Btn1Type1 type="button" onClick={handleSelectModifyNumCheck}>
              수정
            </BS.Btn1Type1>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
            <ExcelDownloadButton
              headers={[
                { label: '출입장소 번호', key: 'place_id' },
                { label: '출입장소', key: 'name' },
                { label: '주소', key: 'address' },
                { label: '상세 주소', key: 'detail' },
                { label: '관리자', key: 'manager' },
                { label: '연락처', key: 'phone' },
              ]}
              data={placeAllList}
              fileName="출입 장소 목록"
              sheetName="출입 장소 목록"
            />
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === placeListState?.length &&
                        placeListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="./img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>출입장소 번호</th>
                <th>출입장소</th>
                <th>주소</th>
                <th>관리자</th>
                <th>연락처</th>
                <th>대시보드 설정</th>
              </tr>
            </thead>
            <tbody>
              {placeListState?.map((place: PlaceDto, index) => (
                <tr key={place.id}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(place.id))
                        }
                        checked={
                          checkItems.includes(Number(place.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="./img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td
                    className="button-text"
                    onClick={() => handlePlaceModify(Number(place.id))}
                  >
                    {place.place_id}
                  </td>
                  <td
                    className="button-text"
                    onClick={() => handlePlaceModify(Number(place.id))}
                  >
                    {place.name}
                  </td>
                  <td>
                    {place.address} {place.detail}
                  </td>
                  <td>{place.manager}</td>
                  <td>{place.phone}</td>
                  <td>
                    <IS.CtrlBox>
                      <input
                        type="checkbox"
                        id={`dcv-${index}`}
                        // ref={autoBtnRef}
                        checked={place.is_active === 0 ? false : true}
                        onChange={(e) =>
                          handleDashboardCheckBoxChange(place, e.target.checked)
                        }
                      />
                      <label htmlFor={`dcv-${index}`}></label>
                    </IS.CtrlBox>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {placeListLengthState !== 0 && (
          <Pagenation
            listAllLength={placeListLengthState}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {placeAddModalState && <PlaceAddModal />}
      {placeModifyModalState && <PlaceModifyModal />}
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
    </>
  );
};

export default PlaceManagement;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;
